/**
 * HistoryPanel.tsx — the deal drawer's History tab on its own route
 * (2026-09-26, returning patients S4.5; clinic S4.2 #225, CTO Q6/Q7/Q8, plan
 * Addenda 2 and 4).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHAT IT ANSWERS: "has this person been here before, why did it stop, and is
 * that answer different now?" — for a clinician about to review a card. The
 * rows come from `GET /v1/clinic/deals/:id/history`, each saying HOW it was
 * matched to this card.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ⚠️ THE ROUTE IS DARK ON EVERY ENVIRONMENT, AND DARK MEANS "SHOW NOTHING".
 * ══════════════════════════════════════════════════════════════════════════
 *
 * clinic's CONTACT_HISTORY_ENABLED is OFF by default and stays off until the
 * DPIA is signed off and identity's dispute producer is live (Addendum 2 / 4).
 * While it is off both routes answer 404 `{code:'disabled'}` — and so does a
 * clinic-service older than S4.2, with a bare 404. Either way this view is
 * HIDDEN: `useDealHistory` reports `unavailable` and the drawer keeps exactly
 * the History lists it had before S4.5 (Previous attempts / Previous deals,
 * which come from the deal aggregate and were never behind the switch). A
 * dark route must never read as an outage, and it must never read as
 * "nothing earlier" either.
 *
 * ⚠️ THE SHAPE IS THE SERVER'S CONTROL; THIS FILE ONLY RESPECTS IT (R41).
 * An `email_unverified` row arrives with NO questionnaire id, NO card id and NO
 * stop reason — the only handle on it is an opaque entry id. So an email-only
 * row renders its dates, its coarse outcome and the server's own words
 * "email match — not verified", and NOTHING that opens or names the record.
 * Its detail sits behind the LOGGED REVEAL:
 *  - the reveal happens ONLY on a click, never on render, never on a hover,
 *    never prefetched (a spec pins that opening the tab POSTs nothing);
 *  - the click opens a dialog that asks for the REQUIRED reason (the server's
 *    closed enum) and an optional note — REQUIRED for "Other" — BEFORE any
 *    request; the server writes `assessment_history_answers_viewed` in the
 *    same transaction before it reads a single answer;
 *  - the button is described by the visible sentence "Opening this is
 *    recorded in the audit log", so a screen-reader user hears the
 *    consequence before pressing it.
 *
 * A `same_account` / `same_journey` row carries its stop inline (plan S4.2).
 * ⚠️ Only `same_account` is PROOF; `same_journey` rests on browser-held journey
 * ids a holder could forge (Addendum 4) — so no row here is ever called
 * "proven" or "verified", and the basis is always written out in words.
 *
 * ROLE-SHAPED (Q8): only an ADMIN's answer carries `touches` (engagement,
 * recovery emails, the consent ledger) and `unlockLinksIssued`. The section is
 * gated on BOTH the role and the key — a clinician answer that somehow carried
 * touches still renders none (defence in depth; the clinic parse is the
 * control). Clinicians keep the consent STATUS.
 *
 * R19: nothing here logs, events or puts anything in a URL. The note typed in
 * the reveal dialog is staff free text for the audit row only.
 */
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { ApiError } from '../api/client';
import { getDealHistory, historyUnavailable, revealDealHistoryEntry } from '../api/deals';
import { isServicePrincipal, lookupStaffDisplayMap } from '../api/identity';
import type {
  DealHistory,
  DealHistoryEntry,
  DealHistoryRevealReason,
  DealHistoryRevealResult,
  DealHistoryStop,
  DealHistoryStopCategory,
  DealHistoryTouchKind,
  UserRole,
} from '../api/types';
import { formatUtcDate, formatUtcDateTime } from '../lib/format';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Alert, Badge, Button, Field, Help, Select } from '../ui/kit';
import { SectionTitle } from '../ui/sections';
import { DealLinkButton } from './duplicateSurface';
import { CONSENT_SOURCE_LABELS } from './MarketingConsentSection';
import { MATCH_BASIS_LABEL } from './returning';
import { STAGE_LABEL } from './stages';

/* ── The read ──────────────────────────────────────────────────────────────── */

export type DealHistoryLoad =
  | { state: 'idle' }
  | { state: 'loading' }
  /** 404 — the switch is off, or the route does not exist on this server. HIDE the view. */
  | { state: 'unavailable' }
  /** Anything else — an outage. Say so; never "nothing earlier". */
  | { state: 'error' }
  | { state: 'ready'; history: DealHistory };

/**
 * The History tab's read, owned by the DRAWER so that the answer — and every
 * answer revealed from it — survives switching tabs. A reveal writes an audit
 * row; losing its result to a tab switch would make the reader open it again
 * and write a second row for one look.
 *
 * `enabled` is "the History tab has been opened": the read fires the first time
 * it is true and never before, so a drawer opened on any other tab asks clinic
 * nothing about this contact's history.
 */
export function useDealHistory(dealId: string, enabled: boolean) {
  const [load, setLoad] = useState<DealHistoryLoad>({ state: 'idle' });
  const [revealed, setRevealed] = useState<Record<string, DealHistoryRevealResult>>({});
  const [nonce, setNonce] = useState(0);
  /**
   * WHICH READ IS CURRENT — `<dealId>#<nonce>`. One read per card per retry,
   * however often the tab is switched away and back. NOT cancelled when the tab
   * is left mid-read: the answer still lands (a reader who glanced at another
   * tab must not come back to a spinner that can never finish), and it is
   * dropped only when a NEWER read has started or the card has changed.
   */
  const current = useRef<string | null>(null);

  // A different card is a different history: start again from nothing.
  useEffect(() => {
    current.current = null;
    setLoad({ state: 'idle' });
    setRevealed({});
  }, [dealId]);

  useEffect(() => {
    if (!enabled) return;
    const key = `${dealId}#${nonce}`;
    if (current.current === key) return;
    current.current = key;
    setLoad({ state: 'loading' });
    getDealHistory(dealId).then(
      (history) => {
        if (current.current === key) setLoad({ state: 'ready', history });
      },
      (error: unknown) => {
        if (current.current === key) setLoad({ state: historyUnavailable(error) ? 'unavailable' : 'error' });
      },
    );
  }, [dealId, enabled, nonce]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);
  const remember = useCallback(
    (result: DealHistoryRevealResult) => setRevealed((prev) => ({ ...prev, [result.entryId]: result })),
    [],
  );
  return { load, revealed, retry, remember };
}

/* ── Words ─────────────────────────────────────────────────────────────────── */

/** The coarse stop band in words — never the raw stopKey (GDPR-3). */
export const STOP_CATEGORY_LABEL: Record<DealHistoryStopCategory, string> = {
  safety_condition: 'A health condition we cannot treat safely',
  bmi_floor: 'BMI below the level we can treat',
  bmi_gate: 'BMI below the treatment threshold',
  age: 'Age',
  pregnancy: 'Pregnancy, breastfeeding or trying to conceive',
  clinical: 'A clinical reason',
};

/** How an earlier attempt ended — the drawer's "Previous attempts" words, one vocabulary. */
const OUTCOME_LABEL: Record<string, string> = {
  in_progress: 'Still open',
  superseded: 'They started again before this one was closed',
  not_proceeding: 'Said they were not going ahead',
  closed: 'Closed by staff',
  parked: 'Never reached — call attempts ran out',
  not_eligible: 'Ended not eligible',
  eligible: 'Came back eligible',
  submitted: 'Submitted',
  abandoned: 'Started and never finished',
};

const KIND_LABEL: Record<DealHistoryEntry['kind'], string> = {
  questionnaire: 'Questionnaire',
  callback: 'Callback',
};

export const TOUCH_KIND_LABEL: Record<DealHistoryTouchKind, string> = {
  guide_emailed: 'Guide emailed',
  whatsapp_logged: 'WhatsApp message logged',
  recovery_email_sent: 'Follow-up email sent (automatic sequence)',
  consent_granted: 'Marketing consent given',
  consent_withdrawn: 'Marketing consent withdrawn',
  consent_reaffirmed: 'Marketing consent re-confirmed',
};

/** The reveal reasons, in clinic's order (`HISTORY_REVEAL_REASONS`). */
export const REVEAL_REASON_LABEL: Record<DealHistoryRevealReason, string> = {
  clinical_review: 'Reviewing this patient',
  safety_check: 'A safety check',
  patient_request: 'The patient asked about it',
  other: 'Other (add a note)',
};
const REVEAL_REASONS = Object.keys(REVEAL_REASON_LABEL) as DealHistoryRevealReason[];

/** clinic's `HISTORY_REVEAL_NOTE_MAX` — the note is a short reason, not a clinical record. */
export const REVEAL_NOTE_MAX = 200;

/** The one sentence every reveal button is described by. */
export const REVEAL_IS_LOGGED = 'Opening this is recorded in the audit log.';

const WITHHELD_LABEL: Record<DealHistoryRevealResult['withheld'][number], string> = {
  date_of_birth: 'date of birth',
  photo: 'photos',
};

/** The basis in words: the server's own label when it sent one, else ours. */
function basisText(entry: Pick<DealHistoryEntry, 'matchBasis' | 'matchLabel'>): string {
  const label = entry.matchLabel?.trim();
  if (label) return label.charAt(0).toUpperCase() + label.slice(1);
  return MATCH_BASIS_LABEL[entry.matchBasis] ?? entry.matchBasis;
}

function answerChangedText(changed: boolean | null): string {
  if (changed === true) return 'This answer is different now';
  if (changed === false) return 'Same answer now';
  return 'Cannot compare with this questionnaire';
}

/* ── Rendering ─────────────────────────────────────────────────────────────── */

const rowStyle: CSSProperties = {
  border: '1px solid var(--gps-border)',
  borderRadius: 'var(--gps-r-md)',
  padding: '10px 12px',
  display: 'grid',
  gap: '6px',
};
const metaStyle: CSSProperties = { fontSize: '13px', color: 'var(--gps-muted)' };

/**
 * A question's WORDING, found in answers already on screen — never the key
 * (2026-09-26, S4.5 review). clinic sends only `questionKey` on a stop (the
 * seeded keys are q0..q18), and "Stopping question: q1" tells a clinician
 * nothing — CTO Q6 asks to show "the question that stopped it". The estate's
 * rule is the Review tab's `promptFor` (ReviewView.tsx): resolve the key
 * against the rendered sections, or say the weaker true thing.
 *
 * The sources, most exact first: the revealed answers of THAT questionnaire
 * (`own`), then any other answers revealed in this view (the same clinic's
 * question set — the precedent `promptFor` sets for prior stops). No extra
 * read is made for wording: the only other source would be this card's full
 * review, which carries every answer and a signed photo link, and fetching
 * that to label one line is more health data than the line is worth.
 */
export function stopQuestionPrompt(
  questionKey: string,
  own: DealHistoryRevealResult['sections'] | null,
  revealed: Record<string, DealHistoryRevealResult>,
): string | null {
  const find = (sections: DealHistoryRevealResult['sections']): string | null => {
    for (const section of sections) {
      const prompt = section.items.find((item) => item.questionKey === questionKey)?.prompt?.trim();
      if (prompt) return prompt;
    }
    return null;
  };
  if (own) {
    const exact = find(own);
    if (exact) return exact;
  }
  for (const result of Object.values(revealed)) {
    const found = find(result.sections);
    if (found) return found;
  }
  return null;
}

/** The weaker true sentence when the wording is not on screen — never the key. */
export const STOP_QUESTION_NOT_SHOWN = 'the question is not shown here';

/**
 * Why it stopped — used inline on a linked row and inside a revealed answer.
 * `prompt` is the question's wording when it could be found (see
 * `stopQuestionPrompt`); `canReveal` adds where the wording is when it could not.
 */
function StopLines({ stop, prompt, canReveal = false }: { stop: DealHistoryStop; prompt: string | null; canReveal?: boolean }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '2px', fontSize: '13.5px' }}>
      <li>
        <strong>Why it stopped:</strong> {STOP_CATEGORY_LABEL[stop.category] ?? 'A clinical reason'}
      </li>
      {stop.questionKey ? (
        <li data-stop-question>
          <strong>Stopping question:</strong>{' '}
          {prompt ? (
            <>&ldquo;{prompt}&rdquo;</>
          ) : (
            <>
              {STOP_QUESTION_NOT_SHOWN}
              {canReveal ? ' (view the earlier answers to see it)' : ''}
            </>
          )}{' '}
          · {answerChangedText(stop.answerChanged)}
        </li>
      ) : (
        <li>No single question stopped it (it was the whole questionnaire).</li>
      )}
      {stop.mhRisk ? <li style={{ color: 'var(--gps-danger-text)', fontWeight: 600 }}>That questionnaire raised a mental-health flag.</li> : null}
    </ul>
  );
}

function RevealedAnswers({
  result,
  revealed,
  headingRef,
  onOpenDeal,
}: {
  result: DealHistoryRevealResult;
  /** Every answer revealed in this view — the wording fallback for the stop. */
  revealed: Record<string, DealHistoryRevealResult>;
  headingRef?: (node: HTMLHeadingElement | null) => void;
  onOpenDeal?: (dealId: string) => void;
}) {
  // h3 under the section's h2, h4 per answer section (axe heading-order).
  const withheld = result.withheld.map((w) => WITHHELD_LABEL[w] ?? w);
  return (
    <div
      style={{ borderTop: '1px solid var(--gps-border)', paddingTop: '8px', marginTop: '2px', display: 'grid', gap: '8px' }}
      data-revealed-entry={result.entryId}
    >
      <h3 ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: '14px', fontWeight: 700, outline: 'none' }}>
        Earlier answers
      </h3>
      <p style={{ ...metaStyle, margin: 0 }}>You opened these just now. It is recorded in the audit log.</p>
      {result.stop ? (
        <StopLines
          stop={result.stop}
          prompt={result.stop.questionKey ? stopQuestionPrompt(result.stop.questionKey, result.sections, revealed) : null}
        />
      ) : null}
      {result.sections.map((section) => (
        <section key={section.key} aria-label={section.title}>
          <h4 style={{ margin: '4px 0', fontSize: '13px', fontWeight: 700, color: 'var(--gps-muted)' }}>{section.title}</h4>
          <dl style={{ margin: 0, display: 'grid', gap: '4px' }}>
            {section.items.map((item) => (
              <div key={item.questionKey}>
                <dt style={{ fontSize: '13px', color: 'var(--gps-muted)' }}>{item.prompt}</dt>
                <dd style={{ margin: 0, fontSize: '14px', color: 'var(--gps-text)' }}>
                  {item.answer}
                  {item.detail ? <span style={{ display: 'block', ...metaStyle }}>{item.detail}</span> : null}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
      {withheld.length > 0 ? (
        <p style={{ ...metaStyle, margin: 0 }}>Not shown here: {withheld.join(' and ')}.</p>
      ) : null}
      {result.dealId && onOpenDeal ? (
        <span>
          <DealLinkButton onClick={() => onOpenDeal(result.dealId as string)} label="Open the card this questionnaire is on">
            Open that card
          </DealLinkButton>
        </span>
      ) : null}
    </div>
  );
}

function EntryRow({
  entry,
  result,
  revealed,
  onReveal,
  onOpenDeal,
  headingRef,
}: {
  entry: DealHistoryEntry;
  result: DealHistoryRevealResult | undefined;
  revealed: Record<string, DealHistoryRevealResult>;
  onReveal: (entry: DealHistoryEntry) => void;
  onOpenDeal?: (dealId: string) => void;
  headingRef: (node: HTMLHeadingElement | null) => void;
}) {
  const noteId = useId();
  const emailOnly = entry.matchBasis === 'email_unverified';
  const outcome = OUTCOME_LABEL[entry.outcome] ?? entry.outcome.replace(/_/g, ' ');
  const ended = entry.endedAt && formatUtcDate(entry.endedAt) !== formatUtcDate(entry.startedAt) ? ` → ${formatUtcDate(entry.endedAt)}` : '';
  return (
    <li style={rowStyle} data-history-entry={entry.entryId} data-match-basis={entry.matchBasis}>
      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
        <Badge tone="neutral">{KIND_LABEL[entry.kind] ?? entry.kind}</Badge>
        <span style={{ fontSize: '14px', color: 'var(--gps-text)' }}>{outcome}</span>
        <span style={metaStyle}>
          {formatUtcDate(entry.startedAt)}
          {ended}
        </span>
      </span>
      <span
        style={{
          fontSize: '12.5px',
          fontWeight: 600,
          color: emailOnly ? 'var(--gps-warn-text)' : 'var(--gps-muted)',
        }}
      >
        {basisText(entry)}
      </span>
      {/* A linked row carries its stop inline (plan S4.2). An email-only row
          has none on the wire, so there is nothing here to hide — the reveal
          below is the only way to it. */}
      {!emailOnly && entry.stop && !result ? (
        <StopLines
          stop={entry.stop}
          prompt={entry.stop.questionKey ? stopQuestionPrompt(entry.stop.questionKey, null, revealed) : null}
          canReveal={entry.revealable}
        />
      ) : null}
      {!emailOnly && entry.dealId && onOpenDeal && !result ? (
        <span>
          <DealLinkButton onClick={() => onOpenDeal(entry.dealId as string)} label="Open the card this questionnaire is on">
            Open that card
          </DealLinkButton>
          {entry.dealStage ? <span style={{ ...metaStyle, marginLeft: '8px' }}>{STAGE_LABEL[entry.dealStage] ?? entry.dealStage}</span> : null}
        </span>
      ) : null}
      {entry.revealable && !result ? (
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
          <Button
            type="button"
            variant="ghost"
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
            aria-describedby={noteId}
            onClick={() => onReveal(entry)}
          >
            View earlier answers
          </Button>
          <span id={noteId} style={metaStyle}>
            {REVEAL_IS_LOGGED}
          </span>
        </span>
      ) : null}
      {result ? <RevealedAnswers result={result} revealed={revealed} headingRef={headingRef} onOpenDeal={onOpenDeal} /> : null}
    </li>
  );
}

/**
 * The route-backed History view. Rendered by the drawer ONLY on a `ready`
 * load — see the file header for why every other state shows the old lists.
 */
export function ContactHistoryView({
  dealId,
  history,
  revealed,
  onRevealed,
  role,
  onOpenDeal,
}: {
  dealId: string;
  history: DealHistory;
  revealed: Record<string, DealHistoryRevealResult>;
  onRevealed: (result: DealHistoryRevealResult) => void;
  role: UserRole;
  onOpenDeal?: (dealId: string) => void;
}) {
  const [pending, setPending] = useState<DealHistoryEntry | null>(null);
  const [reason, setReason] = useState<DealHistoryRevealReason | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusEntry, setFocusEntry] = useState<string | null>(null);
  const reasonId = useId();
  const errorId = useId();
  const headings = useRef<Record<string, HTMLHeadingElement | null>>({});

  // After a reveal lands, the button that opened it is gone (the answers took
  // its place), so focus goes to the answers' own heading — never to <body>.
  useEffect(() => {
    if (!focusEntry) return;
    headings.current[focusEntry]?.focus();
    setFocusEntry(null);
  }, [focusEntry, revealed]);

  function openReveal(entry: DealHistoryEntry) {
    setPending(entry);
    // NO DEFAULT REASON: a pre-picked "Reviewing this patient" would record a
    // reason nobody gave (the resubscribe method radio's rule).
    setReason('');
    setError(null);
  }

  // Focus goes back to the "View earlier answers" button that opened the
  // dialog — DialogShell returns it to its opener on close.
  function cancelReveal() {
    if (busy) return;
    setPending(null);
  }

  function confirmReveal(note?: string) {
    if (!pending || !reason || busy) return;
    setBusy(true);
    setError(null);
    const entry = pending;
    revealDealHistoryEntry(dealId, entry.entryId, { reason, ...(note ? { note } : {}) }).then(
      (result) => {
        setBusy(false);
        setPending(null);
        onRevealed(result);
        setFocusEntry(result.entryId);
      },
      (err: unknown) => {
        setBusy(false);
        // Stays in the open dialog with the reason kept — a refusal is
        // correctable in place (the close-as-lost lesson).
        setError(
          err instanceof ApiError && err.status === 404
            ? 'That earlier questionnaire is no longer in this history. Close this and reopen the History tab.'
            : err instanceof ApiError && err.detail
              ? `We couldn't open it: ${err.detail}`
              : "We couldn't open it just now. Nothing was shown and nothing was recorded that we know of — please try again.",
        );
      },
    );
  }

  const touches = role === 'ADMIN' && Array.isArray(history.touches) ? history.touches : null;
  const emailOnlyPending = pending?.matchBasis === 'email_unverified';

  return (
    <section aria-label="Earlier questionnaires and calls" style={{ marginBottom: 'var(--gps-s-4)' }}>
      <SectionTitle>Earlier questionnaires and calls</SectionTitle>
      {history.emailDisputed ? (
        <Help>
          The owner of this email address told us a sign-up was not theirs, so matches by email alone are left out
          of this list.
        </Help>
      ) : null}
      {history.entries.length === 0 ? (
        <Help>No earlier questionnaires or calls were found for this contact.</Help>
      ) : (
        <ul aria-label="Earlier questionnaires and calls" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px' }}>
          {history.entries.map((entry) => (
            <EntryRow
              key={entry.entryId}
              entry={entry}
              result={revealed[entry.entryId]}
              revealed={revealed}
              onReveal={openReveal}
              onOpenDeal={onOpenDeal}
              headingRef={(node) => {
                headings.current[entry.entryId] = node;
              }}
            />
          ))}
        </ul>
      )}
      {history.truncated ? (
        <p style={{ ...metaStyle, margin: '8px 0 0' }}>Showing the most recent only — there may be more.</p>
      ) : null}

      {/* The consent STATUS — both roles (Q8: clinicians keep seeing it). */}
      <p style={{ fontSize: '13.5px', color: 'var(--gps-text)', margin: '12px 0 0' }}>
        <strong>Marketing permission now:</strong> Email {history.consent.email.granted ? 'given' : 'not given'}
        {history.consent.email.changedAt ? ` (${formatUtcDate(history.consent.email.changedAt)})` : ''} · WhatsApp{' '}
        {history.consent.whatsapp.granted ? 'given' : 'not given'}
        {history.consent.whatsapp.changedAt ? ` (${formatUtcDate(history.consent.whatsapp.changedAt)})` : ''}
      </p>

      {touches ? <AdminTouches touches={touches} unlockLinksIssued={history.unlockLinksIssued} /> : null}

      <ConfirmDialog
        open={pending !== null}
        title="Open earlier answers"
        confirmLabel={busy ? 'Opening…' : 'Open answers'}
        busy={busy}
        // The note is optional, REQUIRED for "Other" — the server's rule
        // (`dealHistoryRevealRequest`); ≥3 characters here, the estate's floor.
        requireReason={reason === 'other'}
        optionalReason={reason !== 'other'}
        reasonLabel="Note"
        reasonMaxLength={REVEAL_NOTE_MAX}
        confirmDisabled={reason === ''}
        confirmDescribedBy={error ? errorId : undefined}
        onCancel={cancelReveal}
        onConfirm={confirmReveal}
        body={
          <>
            <p style={{ margin: '0 0 8px' }}>
              <strong>{REVEAL_IS_LOGGED}</strong> Your name, the time and your reason are kept.
            </p>
            {emailOnlyPending ? (
              <p style={{ margin: '0 0 8px' }}>
                This questionnaire matches only by email address, and that is not verified. It may belong to someone
                else who used the same email.
              </p>
            ) : null}
            <p style={{ margin: '0 0 12px', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
              Date of birth and photos are never shown here. Keep health details out of the note.
            </p>
            <Field label="Why are you opening it?" htmlFor={reasonId}>
              <Select
                id={reasonId}
                value={reason}
                aria-required="true"
                onChange={(event) => setReason(event.target.value as DealHistoryRevealReason | '')}
              >
                <option value="">Choose a reason</option>
                {REVEAL_REASONS.map((code) => (
                  <option key={code} value={code}>
                    {REVEAL_REASON_LABEL[code]}
                  </option>
                ))}
              </Select>
            </Field>
            {error ? (
              <Alert tone="danger" role="alert" id={errorId} style={{ marginBottom: '8px' }}>
                {error}
              </Alert>
            ) : null}
          </>
        }
      />
    </section>
  );
}

/**
 * ADMIN ONLY (Q8) — every marketing and engagement contact with this address.
 * A touch carries no card id on purpose (it would be an unlogged route to a
 * possibly-stranger's card); `onThisCard` is all it says about where.
 */
function AdminTouches({
  touches,
  unlockLinksIssued,
}: {
  touches: NonNullable<DealHistory['touches']>;
  unlockLinksIssued: number | undefined;
}) {
  const [names, setNames] = useState<Record<string, string>>({});
  const idsKey = [...new Set(touches.map((t) => t.actorUserId).filter((id): id is string => !!id && !isServicePrincipal(id)))]
    .sort()
    .join(',');

  useEffect(() => {
    if (!idsKey) return;
    let cancelled = false;
    lookupStaffDisplayMap(idsKey.split(','))
      .then((map) => {
        if (!cancelled) setNames(map);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return (
    <div style={{ marginTop: 'var(--gps-s-4)' }}>
      <SectionTitle>Contact and marketing (admins only)</SectionTitle>
      {typeof unlockLinksIssued === 'number' ? (
        <p style={{ ...metaStyle, margin: '0 0 8px' }}>
          Unlock links sent for this contact: {unlockLinksIssued}
        </p>
      ) : null}
      {touches.length === 0 ? (
        <Help>No marketing or engagement contact recorded for this address.</Help>
      ) : (
        <ol aria-label="Contact and marketing history" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {touches.map((touch, index) => {
            const by = touch.actorUserId && !isServicePrincipal(touch.actorUserId) ? names[touch.actorUserId] : undefined;
            return (
              <li
                key={`${touch.at}-${touch.kind}-${index}`}
                style={{ padding: '8px 0', borderTop: '1px solid var(--gps-border)', fontSize: '13.5px', color: 'var(--gps-text)' }}
              >
                <span style={{ fontWeight: 600 }}>{TOUCH_KIND_LABEL[touch.kind] ?? touch.kind.replace(/_/g, ' ')}</span>
                {touch.channel ? ` · ${touch.channel === 'whatsapp' ? 'WhatsApp' : 'Email'}` : ''}
                {touch.source ? ` · ${CONSENT_SOURCE_LABELS[touch.source] ?? 'Source not recognised'}` : ''}
                <span style={{ display: 'block', color: 'var(--gps-muted)' }}>
                  {formatUtcDateTime(touch.at)} · {touch.onThisCard ? 'on this card' : 'on another card'}
                  {by ? ` · by ${by}` : ''}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
